import {
  Outlet,
  Link,
  createRootRoute,
  getRouteApi,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportClientError } from "../lib/client-error-reporter";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { CookieBanner } from "../components/CookieBanner";
import playfairFont from "../assets/fonts/playfair-latin.woff2.asset.json";
import interFont from "../assets/fonts/inter-latin.woff2.asset.json";
import { getSitePublicData, type SitePublicData } from "../lib/site-config.functions";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportClientError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  loader: () => getSitePublicData(),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "El Pajar de Benito · Casa rural con encanto en Guadalajara" },
      {
        name: "description",
        content:
          "Casa rural con encanto en El Ordial, Sierra del Alto Rey (Guadalajara). Naturaleza, tranquilidad y autenticidad a 2 horas de Madrid.",
      },
      { name: "author", content: "El Pajar de Benito" },
      { property: "og:site_name", content: "El Pajar de Benito" },
      { property: "og:title", content: "El Pajar de Benito · Casa rural con encanto en Guadalajara" },
      { property: "og:description", content: "- El Pajar de Benito is a web application for a rural guesthouse, facilitating direct bookings and an enhanced online presence." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "El Pajar de Benito · Casa rural con encanto en Guadalajara" },
      { name: "twitter:description", content: "- El Pajar de Benito is a web application for a rural guesthouse, facilitating direct bookings and an enhanced online presence." },
      { property: "og:image", content: "https://elpajardebenito.es/__l5e/assets-v1/ea730025-2459-4a71-8062-381c008b112b/hero-sierra-1280.webp" },
      { property: "og:url", content: "https://elpajardebenito.es" },
      { name: "twitter:image", content: "https://elpajardebenito.es/__l5e/assets-v1/ea730025-2459-4a71-8062-381c008b112b/hero-sierra-1280.webp" },
      { name: "google-site-verification", content: "TrDa4tF3rvq53T8ecuv7JHgiUDHnDwbAlJI2YmMvqRk" },
      { httpEquiv: "content-language", content: "es-ES" },
      { name: "language", content: "Spanish" },
      { name: "application-name", content: "El Pajar de Benito" },
      { name: "apple-mobile-web-app-title", content: "El Pajar de Benito" },
      { name: "theme-color", content: "#3b2a1f" },
      { name: "format-detection", content: "telephone=no" },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { name: "description", content: "- El Pajar de Benito is a web application for a rural guesthouse, facilitating direct bookings and an enhanced online presence." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "preconnect", href: "https://fevwuheiuewihjbpfmxv.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://fevwuheiuewihjbpfmxv.supabase.co" },
      { rel: "alternate", hrefLang: "es-ES", href: "https://elpajardebenito.es" },
      { rel: "alternate", hrefLang: "es", href: "https://elpajardebenito.es" },
      { rel: "alternate", hrefLang: "x-default", href: "https://elpajardebenito.es" },
      {
        rel: "preload",
        href: playfairFont.url,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: interFont.url,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          "@id": "https://elpajardebenito.es/#lodging",
          name: "El Pajar de Benito",
          description:
            "Casa rural de piedra rehabilitada para grupos hasta 14 personas en El Ordial, Sierra del Alto Rey (Guadalajara). 6 habitaciones con baño privado, salón con chimenea y cocina equipada.",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Calle la Poza, 10",
            addressLocality: "El Ordial",
            addressRegion: "Guadalajara",
            postalCode: "19244",
            addressCountry: "ES",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 41.0508,
            longitude: -3.0386,
          },
          telephone: "+34 641 696 242",
          email: "hola@elpajardebenito.es",
          url: "https://elpajardebenito.es",
          priceRange: "€€",
          image: [
            "https://elpajardebenito.es/__l5e/assets-v1/ea730025-2459-4a71-8062-381c008b112b/hero-sierra-1280.webp",
          ],
          numberOfRooms: 7,
          maximumAttendeeCapacity: 14,
          petsAllowed: false,
          checkinTime: "16:00",
          checkoutTime: "12:00",
          amenityFeature: [
            { "@type": "LocationFeatureSpecification", name: "Baño privado en cada habitación", value: true },
            { "@type": "LocationFeatureSpecification", name: "Chimenea de leña", value: true },
            { "@type": "LocationFeatureSpecification", name: "Cocina equipada", value: true },
            { "@type": "LocationFeatureSpecification", name: "Calefacción", value: true },
            { "@type": "LocationFeatureSpecification", name: "WiFi gratis", value: true },
            { "@type": "LocationFeatureSpecification", name: "Patio / jardín", value: true },
            { "@type": "LocationFeatureSpecification", name: "Parking", value: true },
            { "@type": "LocationFeatureSpecification", name: "Ropa de cama incluida", value: true },
          ],
          areaServed: {
            "@type": "AdministrativeArea",
            name: "Sierra del Alto Rey, Guadalajara",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="es">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAppShell = pathname.startsWith("/gestion") || pathname.startsWith("/auth") || pathname.startsWith("/dorsales");
  if (isAppShell) {
    return (
      <div className="min-h-screen bg-background">
        <Outlet />
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
      <CookieBanner />
    </div>
  );
}
